import { useCallback, useEffect } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'
import { FILE_STALE_TIME } from '@/constants/file'

import { userFilesQueryKeys } from '@/hooks/files/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UnhydratedFile } from '@/types/File'
import type { UUID } from '@/types/Id'

interface UseUserFilesArgs<T> {
  orgId?: UUID | ''
  enabled?: boolean
  staleTime?: number
  refetchOnMount?: boolean
  refetchOnWindowFocus?: boolean
  select?: (data: PaginatedResponse<UnhydratedFile>) => T
}

export const fetchUserFiles = async (
  accessToken: string,
  pagination: PaginateRequest,
  orgId?: UUID | '',
): Promise<PaginatedResponse<UnhydratedFile>> => {
  let url = `${API_URL}/api/v1/user/files`
  const searchParams = new URLSearchParams()
  searchParams.set('limit', pagination.limit.toString())
  if (pagination.offset) {
    searchParams.set('offset', pagination.offset.toString())
  }
  if (!!orgId) {
    searchParams.set('organization_id', orgId)
  }
  url += `?${searchParams.toString()}`
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new Error(res.statusText)
  }
  return (await res.json()) as PaginatedResponse<UnhydratedFile>
}

export const useUserFiles = <T = UnhydratedFile[]>({
  orgId,
  enabled = true,
  staleTime = FILE_STALE_TIME,
  refetchOnMount = true,
  refetchOnWindowFocus = true,
  select,
}: UseUserFilesArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const fetchFn = useCallback(async () => {
    return fetchUserFiles(accessToken, { limit: 10000 }, orgId)
  }, [accessToken, orgId])

  const selectFn = useCallback(
    (data: PaginatedResponse<UnhydratedFile>) => {
      if (select) {
        return select(data)
      }
      return data.data.sort((a, b) =>
        a.name.localeCompare(b.name),
      ) as unknown as T
    },
    [select],
  )

  const query = useQuery({
    queryFn: fetchFn,
    queryKey: userFilesQueryKeys.allOrOrganization({ orgId }),
    staleTime,
    refetchOnMount,
    refetchOnWindowFocus,
    enabled,
    select: selectFn,
  })

  return query
}

interface UseOrganizationFilesPrefetchArgs {
  orgId: UUID | ''
}

export const useOrganizationFilesPrefetch = ({
  orgId,
}: UseOrganizationFilesPrefetchArgs) => {
  const { accessToken } = useAuthInfoWithThrow()

  const queryClient = useQueryClient()

  useEffect(() => {
    queryClient.prefetchQuery({
      queryKey: userFilesQueryKeys.organization({ orgId }),
      queryFn: () => fetchUserFiles(accessToken, { limit: 10000 }, orgId),
    })
  }, [orgId, accessToken, queryClient])
}

interface UseUserFilesCountArgs {
  orgId?: UUID | ''
}

const selectUserFilesCount = (data: PaginatedResponse<UnhydratedFile>) => {
  return data.count
}

export const useUserFilesCount = ({ orgId }: UseUserFilesCountArgs) => {
  return useUserFiles({ orgId, select: selectUserFilesCount })
}
